// The schedule's board (F520, decision 128): the aircraft as rows, the day's
// hours (UTC) across, a bar per slot in the pilot's colour, the free
// stretches as dashed boxes you click, the now-line. The popover that books
// a half hour, the card of a slot, the countdown of an open one. Third
// round: the half hour under the mouse drawn before the click and the
// picked one while the popover is open; the slots that are over listed
// under the calendar, not drawn on it; the aircraft linked from the lane
// and the card.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { api, queries, unwrap, type Slot } from "@/api";
import { ErrorNote } from "@/components/ErrorNote";
import { pilotName } from "@/lib/format";
import { SLOT_MIN, addMinutes, countdown, freeStretches, inWords, localClockOf, pct, slotAtClick, utcClock, utcDayLabel, type Span } from "@/lib/schedule";

/** What a lane draws: one aircraft, its live booking, its slots. */
export type Lane = {
  id: string;
  reg: string;
  name: string;
  /** The caller books for others when they manage the aircraft. */
  manages: boolean;
  /** The caller may book at all (a pilot or above). */
  canBook: boolean;
  booking: { holder: { id: string; name: string; handle: string }; acquired_at: string; flying: boolean } | null;
  slots: Slot[];
};

const COLOURS = ["#1d6ff2", "#8e5cf0", "#1aa38a", "#c0392b", "#7a5230", "#2a7f9e"];

/** The pilot's colour: the caller orange, everyone else by their id. */
export function pilotColour(id: string, meId: string | undefined): string {
  if (meId && id === meId) return "#e0732b";
  let h = 0;
  for (const c of id) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return COLOURS[h % COLOURS.length]!;
}

/** A slot still to come or under way: drawn on the calendar. The others (cancelled, lapsed, ended) go to the history under it. */
export function onTheCalendar(state: Slot["state"]): boolean {
  return state === "planned" || state === "open" || state === "flying";
}

/** A half hour the pointer hovers or the pilot picked, on one lane. */
export type HalfHour = { laneId: string; startsAt: Date };

/** Where along a free box the pointer is, 0 to 1; 0 without a pointer (the keyboard). */
function fractionOf(e: { currentTarget: HTMLElement; nativeEvent: Event }): number {
  const width = e.currentTarget.clientWidth;
  const x = e.nativeEvent instanceof MouseEvent ? e.nativeEvent.offsetX : 0;
  return width > 0 ? x / width : 0;
}

function busyOf(lane: Lane, now: Date): Span[] {
  const spans: Span[] = lane.slots
    .filter((s) => onTheCalendar(s.state))
    .map((s) => ({ start: new Date(s.starts_at), end: new Date(s.ends_at) }));
  if (lane.booking) spans.push({ start: new Date(lane.booking.acquired_at), end: addMinutes(now, 15) });
  return spans;
}

export function ScheduleBoard({
  lanes,
  dayStart,
  now,
  meId,
  onBook,
  onPick,
  picked = null,
  compact = false,
}: {
  lanes: Lane[];
  dayStart: Date;
  now: Date;
  meId: string | undefined;
  onBook: (lane: Lane, startsAt: Date) => void;
  onPick: (slot: Slot, lane: Lane) => void;
  /** The half hour the popover is booking, drawn solid on its lane. */
  picked?: HalfHour | null;
  compact?: boolean;
}) {
  const dayEnd = addMinutes(dayStart, 24 * 60);
  // The half hour under the pointer, drawn before the click so the pilot sees what a click books.
  const [hover, setHover] = useState<HalfHour | null>(null);
  const earliest = addMinutes(now, 60);
  const nowPct = pct(now, dayStart);
  const showNow = now >= dayStart && now < dayEnd;
  return (
    <div className="sched" data-testid="schedule-board">
      <div className="sched-row sched-head">
        <div className="sched-ac muted text-xs">{compact ? utcDayLabel(dayStart) : "Your aircraft · hours UTC"}</div>
        <div className="sched-hours" aria-hidden="true">
          {Array.from({ length: 12 }, (_, i) => (
            <span key={i}>{String(i * 2).padStart(2, "0")}</span>
          ))}
          {showNow ? (
            <span className="sched-nowtag" style={{ left: `${nowPct}%` }}>
              now {utcClock(now)} UTC
            </span>
          ) : null}
        </div>
      </div>
      {lanes.map((lane) => {
        const busy = busyOf(lane, now);
        const free = lane.canBook ? freeStretches(busy, dayStart, dayEnd, earliest) : [];
        const pickedHere = picked && picked.laneId === lane.id && picked.startsAt >= dayStart && picked.startsAt < dayEnd ? picked : null;
        const hoverHere = hover && hover.laneId === lane.id && hover.startsAt.getTime() !== pickedHere?.startsAt.getTime() ? hover : null;
        const ghost = (p: HalfHour, kind: "hover" | "picked") => {
          const end = addMinutes(p.startsAt, SLOT_MIN);
          const left = pct(p.startsAt, dayStart);
          return (
            <div key={kind} className={`sched-ghost ${kind === "picked" ? "sched-picked" : ""}`} style={{ left: `${left}%`, width: `${Math.max(1, pct(end, dayStart) - left)}%` }} data-testid={kind === "picked" ? "sched-picked" : "sched-ghost"}>
              <span className="sched-ghosttag" style={left > 80 ? { right: "100%" } : { left: "100%" }}>
                {kind === "picked" ? "booking " : ""}
                {utcClock(p.startsAt)} to {utcClock(end)} UTC
              </span>
            </div>
          );
        };
        return (
          <div className="sched-row" key={lane.id} data-testid={`lane-${lane.reg}`}>
            {!compact ? (
              <div className="sched-ac">
                <Link to="/airframes/$id" params={{ id: lane.id }} className="font-bold" title={`${lane.reg}'s page`} data-testid={`lane-link-${lane.reg}`}>
                  {lane.reg}
                </Link>
                <div className="muted text-xs">
                  {lane.name || "No name yet"}
                  {lane.booking ? ` · ${lane.booking.flying ? `${lane.booking.holder.name} is flying it` : `booked for ${lane.booking.holder.name}`}` : ""}
                </div>
              </div>
            ) : (
              <div className="sched-ac muted text-xs">{lane.booking ? (lane.booking.flying ? `${lane.booking.holder.name} flying` : `booked for ${lane.booking.holder.name}`) : "free"}</div>
            )}
            <div className="sched-lane">
              {showNow ? <div className="sched-past" style={{ width: `${nowPct}%` }} aria-hidden="true" /> : null}
              {showNow ? <div className="sched-now" style={{ left: `${nowPct}%` }} aria-hidden="true" /> : null}
              {showNow && lane.booking && !lane.slots.some((s) => s.state === "open" || s.state === "flying") ? (
                <div
                  className={`sched-bar ${lane.booking.flying ? "sched-flying" : ""}`}
                  style={{ left: `${pct(new Date(lane.booking.acquired_at), dayStart)}%`, width: `${Math.max(1, pct(now, dayStart) - pct(new Date(lane.booking.acquired_at), dayStart))}%`, background: lane.booking.flying ? undefined : pilotColour(lane.booking.holder.id, meId) }}
                  title={`${lane.booking.holder.name} ${lane.booking.flying ? "is flying it" : "has it booked"} since ${utcClock(new Date(lane.booking.acquired_at))} UTC`}
                >
                  {lane.booking.holder.name}
                  <small>{lane.booking.flying ? "flying" : "booked"} since {utcClock(new Date(lane.booking.acquired_at))}</small>
                </div>
              ) : null}
              {free.map((s) => (
                <button
                  key={s.start.toISOString()}
                  type="button"
                  className="sched-free"
                  style={{ left: `${pct(s.start, dayStart)}%`, width: `${Math.max(0.5, pct(s.end, dayStart) - pct(s.start, dayStart))}%` }}
                  aria-label={`${lane.reg}: free from ${utcClock(s.start)} to ${utcClock(s.end)} UTC, click to book a half hour`}
                  onClick={(e) => onBook(lane, slotAtClick(s, fractionOf(e)))}
                  onMouseMove={(e) => setHover({ laneId: lane.id, startsAt: slotAtClick(s, fractionOf(e)) })}
                  onMouseLeave={() => setHover(null)}
                  onFocus={() => setHover({ laneId: lane.id, startsAt: slotAtClick(s, 0) })}
                  onBlur={() => setHover(null)}
                >
                  free
                </button>
              ))}
              {pickedHere ? ghost(pickedHere, "picked") : null}
              {hoverHere ? ghost(hoverHere, "hover") : null}
              {lane.slots.filter((s) => onTheCalendar(s.state)).map((s) => {
                const start = new Date(s.starts_at);
                const end = new Date(s.ends_at);
                return (
                  <button
                    key={s.id}
                    type="button"
                    className={`sched-bar sched-slot ${s.state === "open" ? "sched-open" : ""}`}
                    style={{ left: `${pct(start, dayStart)}%`, width: `${Math.max(1, pct(end, dayStart) - pct(start, dayStart))}%`, background: pilotColour(s.pilot.id, meId) }}
                    aria-label={`${s.yours ? "Your slot" : `${s.pilot.name}'s slot`} on ${lane.reg} at ${utcClock(start)} UTC${s.second_pilot ? `, with ${s.second_pilot.name} in the right seat` : ""}, ${s.state}`}
                    onClick={() => onPick(s, lane)}
                    data-testid={`slot-${s.id}`}
                  >
                    {s.yours ? "you" : s.pilot.name}
                    {s.second_pilot ? <span className="font-normal"> + {s.second_pilot.name}</span> : null}
                    <small>
                      {utcClock(start)}
                      {s.state === "open" ? ` · ${countdown(end, now)} to fly` : s.state === "flying" ? " · flying" : ""}
                    </small>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** What became of a slot that is over: "cancelled at 13:03 UTC", "lapsed at 15:00 UTC, nobody flew", "the booking ended at 16:10 UTC". */
export function historyWord(slot: Pick<Slot, "state" | "ended_at">): string {
  const at = slot.ended_at ? ` at ${utcClock(new Date(slot.ended_at))} UTC` : "";
  if (slot.state === "cancelled") return `cancelled${at}`;
  if (slot.state === "lapsed") return `lapsed${at}, nobody flew`;
  return `the booking ended${at}`;
}

/** The slots that are over, listed under the calendar: cancelled, lapsed, ended. Nothing when there are none. */
export function SlotHistory({ items, meId, showDay = false }: { items: { slot: Slot; lane: Lane }[]; meId: string | undefined; showDay?: boolean }) {
  const over = items.filter((i) => !onTheCalendar(i.slot.state)).sort((a, b) => a.slot.starts_at.localeCompare(b.slot.starts_at));
  if (over.length === 0) return null;
  return (
    <section className="space-y-1" data-testid="schedule-history" aria-label="Earlier">
      <div className="label">Earlier</div>
      <ul className="space-y-1 text-sm">
        {over.map(({ slot, lane }) => {
          const start = new Date(slot.starts_at);
          return (
            <li key={slot.id} className="flex flex-wrap items-center gap-x-2" data-testid={`history-${slot.id}`}>
              <i className="legend-swatch" style={{ background: pilotColour(slot.pilot.id, meId), opacity: 0.6 }} />
              <Link to="/airframes/$id" params={{ id: lane.id }} className="font-semibold">
                {lane.reg}
              </Link>
              <span className="tnum">
                {showDay ? `${utcDayLabel(start)} · ` : ""}
                {utcClock(start)} to {utcClock(new Date(slot.ends_at))} UTC
              </span>
              <span className="muted">
                {slot.yours ? "you" : slot.pilot.name}
                {slot.second_pilot ? ` + ${slot.second_pilot.name}` : ""} · {historyWord(slot)}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** The popover on a free half hour: the frame in UTC and on this clock, the pilot and the right seat, Book the slot. */
export function SlotPopover({ lane, startsAt, meId, onDone, onClose }: { lane: Lane; startsAt: Date; meId: string | undefined; onDone: (slot: Slot) => void; onClose: () => void }) {
  const queryClient = useQueryClient();
  const endsAt = addMinutes(startsAt, SLOT_MIN);
  const members = useQuery(queries.members(lane.id, "active"));
  const [pilotId, setPilotId] = useState<string>(meId ?? "");
  const [secondId, setSecondId] = useState<string>("");
  const book = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.POST("/api/v1/airframes/{id}/slots", {
          params: { path: { id: lane.id } },
          body: { starts_at: startsAt.toISOString(), pilot_id: lane.manages && pilotId && pilotId !== meId ? pilotId : undefined, second_pilot_id: secondId || undefined },
        }),
      ),
    onSuccess: (slot) => {
      queryClient.invalidateQueries({ queryKey: ["schedule"] });
      queryClient.invalidateQueries({ queryKey: ["slots"] });
      queryClient.invalidateQueries({ queryKey: ["airframes"] });
      onDone(slot);
    },
  });
  const pilots = (members.data ?? []).filter((m) => m.role !== "guest");
  const others = (members.data ?? []).filter((m) => m.id !== (pilotId || meId));
  const pilotName_ = pilotId && pilotId !== meId ? (members.data ?? []).find((m) => m.id === pilotId) : undefined;
  return (
    <section className="card space-y-3 sched-popover" data-testid="slot-popover" aria-label="Book the slot">
      <div>
        <div className="font-bold text-[1.0714rem]" data-testid="popover-frame">
          Book {lane.reg} · {utcClock(startsAt)} to {utcClock(endsAt)} UTC
        </div>
        <div className="muted text-xs" data-testid="popover-local">
          {utcDayLabel(startsAt)} · {localClockOf(startsAt)} to {localClockOf(endsAt)} on your clock
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {lane.manages ? (
          <label className="flex flex-col gap-1 text-sm">
            <span className="label">Pilot</span>
            <select className="input" value={pilotId} onChange={(e) => setPilotId(e.target.value)} data-testid="popover-pilot">
              <option value={meId ?? ""}>You</option>
              {pilots
                .filter((m) => m.id !== meId)
                .map((m) => (
                  <option key={m.id} value={m.id}>
                    {pilotName(m)}
                  </option>
                ))}
            </select>
          </label>
        ) : null}
        <label className="flex flex-col gap-1 text-sm">
          <span className="label">Right seat</span>
          <select className="input" value={secondId} onChange={(e) => setSecondId(e.target.value)} data-testid="popover-second">
            <option value="">Nobody</option>
            {others.map((m) => (
              <option key={m.id} value={m.id}>
                {pilotName(m)}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="muted text-xs">
        {lane.manages ? "You manage " + lane.reg + ": the pilot can be a member, and a second pilot can sit in the right seat. " : "A second pilot can sit in the right seat. "}
        At {utcClock(startsAt)} UTC the aircraft is booked for {pilotName_ ? pilotName(pilotName_) : "you"}. Start X-Plane and fly by {utcClock(endsAt)}; from the page or the app, ten more minutes at a time, until {utcClock(addMinutes(endsAt, 30))}. Not flying by then: the booking lapses.
      </p>
      {lane.booking ? (
        <p className="alert text-xs" data-testid="popover-busy">
          {lane.booking.holder.name} {lane.booking.flying ? "is flying it now" : "has it booked now"}, since {utcClock(new Date(lane.booking.acquired_at))} UTC. Still out at {utcClock(startsAt)}? Your slot waits for the aircraft and lapses at {utcClock(endsAt)} if it is not back by then.
        </p>
      ) : null}
      <ErrorNote error={book.error} />
      <div className="flex items-center gap-3">
        <button type="button" className="btn btn-primary" onClick={() => book.mutate()} disabled={book.isPending} data-testid="popover-book">
          Book the slot
        </button>
        <button type="button" className="btn" onClick={onClose} data-testid="popover-close">
          Not now
        </button>
      </div>
    </section>
  );
}

/** The line on This PC's card while the pilot's slot is open (F520): the countdown and Ten more minutes. */
export function OpenSlotLine({ slot, reg }: { slot: { id: string; starts_at: string; ends_at: string; extended_min: number }; reg: string }) {
  const now = useTicking();
  const queryClient = useQueryClient();
  const extend = useMutation({
    mutationFn: async () => unwrap(await api.POST("/api/v1/slots/{sid}/extend", { params: { path: { sid: slot.id } } })),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["host"] });
      queryClient.invalidateQueries({ queryKey: ["airframes"] });
      queryClient.invalidateQueries({ queryKey: ["schedule"] });
    },
  });
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="slot-line">
      <span className="muted text-xs">
        booked for you by your slot since {utcClock(new Date(slot.starts_at))} UTC · start X-Plane and fly within
      </span>
      <span className="countdown-sm" data-testid="slot-left">
        {countdown(new Date(slot.ends_at), now)}
      </span>
      {slot.extended_min < 30 ? (
        <button type="button" className="btn btn-sm" onClick={() => extend.mutate()} disabled={extend.isPending} aria-label={`Keep ${reg} ten more minutes`} data-testid="slot-extend">
          Ten more minutes
        </button>
      ) : (
        <span className="muted text-xs">an hour at most</span>
      )}
      <ErrorNote error={extend.error} />
    </div>
  );
}

/** A live countdown to a moment, ticking every second. */
export function useTicking(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return now;
}

/** The card of a slot: whose, when (UTC and on this clock), and what the caller can do with it. */
export function SlotCard({ slot, lane, now, onClose }: { slot: Slot; lane: Lane; now: Date; onClose: () => void }) {
  const queryClient = useQueryClient();
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["schedule"] });
    queryClient.invalidateQueries({ queryKey: ["slots"] });
    queryClient.invalidateQueries({ queryKey: ["airframes"] });
  };
  const cancel = useMutation({
    mutationFn: async () => unwrap(await api.POST("/api/v1/slots/{sid}/cancel", { params: { path: { sid: slot.id } } })),
    onSuccess: () => {
      refresh();
      onClose();
    },
  });
  const extend = useMutation({
    mutationFn: async () => unwrap(await api.POST("/api/v1/slots/{sid}/extend", { params: { path: { sid: slot.id } } })),
    onSuccess: refresh,
  });
  const start = new Date(slot.starts_at);
  const end = new Date(slot.ends_at);
  const open = slot.state === "open";
  return (
    <section className="card space-y-2 sched-popover" data-testid="slot-card" aria-label="The slot">
      <div className="flex items-center gap-2">
        <span className={`pill ${open ? "pill-ok" : slot.state === "planned" ? "" : "pill-warn"}`}>{open ? (slot.yours ? "booked for you" : `booked for ${slot.pilot.name}`) : slot.state === "planned" ? (slot.yours ? "your slot" : `${slot.pilot.name}'s slot`) : slot.state}</span>
        <button type="button" className="iconbtn ml-auto" aria-label="Close" onClick={onClose}>
          ×
        </button>
      </div>
      <div className="font-bold text-[1.0714rem]">
        {lane.reg} · {utcClock(start)} to {utcClock(end)} UTC
      </div>
      <div className="muted text-xs">
        {utcDayLabel(start)} · {localClockOf(start)} to {localClockOf(end)} on your clock
        {slot.second_pilot ? ` · ${slot.second_pilot.name} in the right seat` : ""}
        {slot.booked_by.id !== slot.pilot.id ? ` · booked by ${slot.booked_by.name}` : ""}
      </div>
      {open ? (
        <div className="space-y-1">
          <div className="muted text-xs">{slot.yours ? "Start X-Plane and fly within" : "To start flying"}</div>
          <div className="countdown" data-testid="slot-countdown">
            {countdown(end, now)}
          </div>
          {slot.yours ? (
            <p className="muted text-xs">
              {slot.extended_min} of 30 minutes added. You are present on this page, so you can keep it ten more minutes at a time. The booking is cancelled from the app on your PC, where the files are.
            </p>
          ) : null}
        </div>
      ) : slot.state === "planned" ? (
        <p className="muted text-xs">
          {slot.yours ? "Yours when it opens" : "Opens"} in {inWords(start, now)}: the aircraft is booked for {slot.yours ? "you" : slot.pilot.name} then.
        </p>
      ) : null}
      <ErrorNote error={cancel.error ?? extend.error} />
      <div className="flex flex-wrap items-center gap-2">
        {open && slot.yours && slot.extended_min < 30 ? (
          <button type="button" className="btn btn-primary btn-sm" onClick={() => extend.mutate()} disabled={extend.isPending} data-testid="slot-extend">
            Ten more minutes
          </button>
        ) : null}
        {open && slot.yours && slot.extended_min >= 30 ? <span className="muted text-xs">An hour is the most a slot can hold.</span> : null}
        {slot.state === "planned" && slot.can_cancel ? (
          <button type="button" className="btn btn-sm" onClick={() => cancel.mutate()} disabled={cancel.isPending} data-testid="slot-cancel">
            Cancel the slot
          </button>
        ) : null}
        <Link to="/airframes/$id" params={{ id: lane.id }} className="btn btn-sm" data-testid="slot-airframe">
          See {lane.reg}
        </Link>
      </div>
    </section>
  );
}
